/**
 * A React Router `ErrorResponse` (thrown from a loader/action, surfaced by
 * `useRouteError()`) has the shape `{ status, statusText, internal, data }`. It is
 * not an `Error` and carries none of `.error` / `.originalError` / `.message`, so it
 * would otherwise fall through to the `Unexpected error type` branch and produce a
 * misleading, non-actionable Sentry title.
 */
function isErrorResponse(error: object): error is { data?: unknown; status: number; statusText?: string } {
  return 'status' in error && typeof (error as { status: unknown }).status === 'number';
}

/**
 * Normalise whatever Remix surfaces from `useRouteError()` into an Error suitable
 * for Sentry capture. Route errors can be Error instances, thrown Responses/
 * `ErrorResponse`s, or arbitrary objects.
 */
export function extractException(error: unknown): Error {
  if (!error) {
    return new Error('Unknown error caught by RootErrorBoundary');
  }
  if (error instanceof Error) {
    return error;
  }
  const err = error as { error?: Error; message?: string; originalError?: Error };
  if (err.error || err.originalError) {
    return err.error || err.originalError!;
  }
  if (err.message) {
    return new Error(err.message);
  }
  // React Router ErrorResponse ({ status, statusText, data }) — give it a meaningful
  // title (e.g. "Route error 500") instead of "Unexpected error type: …".
  if (typeof error === 'object' && isErrorResponse(error)) {
    const suffix = error.statusText ? `: ${error.statusText}` : '';
    return new Error(`Route error ${error.status}${suffix}`);
  }
  // Non-object error (string, number, etc.) — wrap so Sentry always receives a real Error with a stack.
  return new Error(`Unexpected error type: ${JSON.stringify(error)}`);
}
